<script setup lang="ts">
import { Check, X } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import FileAttachmentList from '@/components/files/FileAttachmentList.vue';
import { formatDate } from '~/lib/dateFormat';
import type { Approval } from '~/types/models';

const props = defineProps<{
    open: boolean,
    approval?: Approval | null,
    saving?: boolean,
}>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void,
    (e: 'save', payload: { approval: Approval, status: 'pending' | 'approved' | 'rejected', note: string, message: string }): void,
}>();

const note = ref('');
const message = ref('');

const hasPreviousResponse = computed(() => props.approval?.status === 'approved' || props.approval?.status === 'rejected');

watch(
    () => [props.open, props.approval?.id, props.approval?.reviewNote, props.approval?.message] as const,
    ([open]) => {
        if (!open) return;
        note.value = String(props.approval?.reviewNote || '').trim();
        message.value = String(props.approval?.message || '').trim();
    },
    { immediate: true },
);

function save(status: 'pending' | 'approved' | 'rejected') {
    if (!props.approval) return;
    emit('save', {
        approval: props.approval,
        status,
        note: note.value.trim(),
        message: message.value.trim(),
    });
}
</script>

<template>
    <Dialog :open="open" @update:open="(value) => emit('update:open', value)">
        <DialogContent class="w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-5 sm:max-w-2xl sm:p-6">
            <DialogHeader>
                <DialogTitle>{{ approval?.stageName || 'Approval response' }}</DialogTitle>
                <DialogDescription>
                    {{ hasPreviousResponse ? 'Update the decision or reword the internal note saved against this approval.' : 'Respond to the approval request and optionally add a note for the team.' }}
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4">
                <div class="grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
                    <div class="rounded-xl border border-border/70 bg-background/70 px-3 py-3">
                        <p class="text-xs uppercase tracking-[0.14em] text-muted-foreground/80">Current status</p>
                        <div class="mt-2">
                            <Badge :variant="approval?.status === 'approved' ? 'default' : approval?.status === 'rejected' ? 'destructive' : 'secondary'">
                                {{ approval?.status || 'pending' }}
                            </Badge>
                        </div>
                    </div>
                    <div class="rounded-xl border border-border/70 bg-background/70 px-3 py-3">
                        <p class="text-xs uppercase tracking-[0.14em] text-muted-foreground/80">Requested by</p>
                        <p class="mt-2 font-medium text-foreground">{{ approval?.requestedByName || 'Unknown' }}</p>
                    </div>
                    <div class="rounded-xl border border-border/70 bg-background/70 px-3 py-3">
                        <p class="text-xs uppercase tracking-[0.14em] text-muted-foreground/80">Last updated</p>
                        <p class="mt-2 font-medium text-foreground">{{ approval?.respondedAt ? formatDate(approval.respondedAt, 'datetime') : 'Not responded yet' }}</p>
                    </div>
                </div>

                <div class="space-y-2">
                    <label class="text-sm font-medium text-foreground">Client-facing request</label>
                    <RichTextEditor
                        v-model="message"
                        density="compact"
                        placeholder="Explain what you need the client to review or approve."
                        helper-text="This is visible to the client with the approval request."
                    />
                </div>

                <FileAttachmentList :attachments="approval?.attachments" />

                <div class="space-y-2">
                    <label class="text-sm font-medium text-foreground">Internal review note</label>
                    <Textarea
                        v-model="note"
                        rows="5"
                        placeholder="Add internal context about this approval decision."
                    />
                </div>
            </div>

            <DialogFooter class="gap-2 sm:justify-between">
                <Button type="button" variant="ghost" :disabled="saving" @click="save(approval?.status || 'pending')">
                    {{ saving ? 'Saving...' : 'Update' }}
                </Button>
                <Button type="button" variant="outline" :disabled="saving" @click="save('rejected')">
                    <X class="mr-1 size-4" />
                    {{ saving ? 'Saving...' : hasPreviousResponse && approval?.status === 'rejected' ? 'Save as rejected' : 'Reject' }}
                </Button>
                <Button type="button" :disabled="saving" @click="save('approved')">
                    <Check class="mr-1 size-4" />
                    {{ saving ? 'Saving...' : hasPreviousResponse && approval?.status === 'approved' ? 'Save as approved' : 'Approve' }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
